import { DialogOverlay, DialogContent } from '@reach/dialog';
import styled, { css } from 'styled-components';

import { FILTER_MENU_CONFIG } from './filterMenuConstants';
import { roundSelectionIndicatorStyles } from './sharedStyles';
import { a11yInvisibleStyle } from '~/components/shared/common/styles';
import { CTA } from '~/components/shared/core/button/CTA';
import { media } from '~/components/shared/core/styles';

export const S = {
  ViewOptionsContainer: styled.div`
    ${({ theme }) => css`
      display: flex;
      align-items: baseline;
      column-gap: ${theme.size[4]};
      justify-content: flex-end;
      background: ${theme.colors.background.base};
      overflow: hidden;
    `}
  `,
  Button: styled(CTA)`
    width: 50%;
    line-height: 16px;
    min-height: 40px;
    text-transform: none;
  `,
  CloseBtn: styled.button`
    ${({ theme }) => css`
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${theme.colors.content.primary};
      cursor: pointer;
      width: ${FILTER_MENU_CONFIG.DIMENSIONS.CLOSE_BUTTON_SIZE}px;
      height: ${FILTER_MENU_CONFIG.DIMENSIONS.CLOSE_BUTTON_SIZE}px;
    `}
  `,
  ResultsStatus: styled.div`
    ${a11yInvisibleStyle}
  `,
  Dialog: styled(DialogOverlay)`
    width: 100vw;
    position: fixed;
    inset: 0;
    background-color: rgba(0 0 0 / 10%);
    z-index: ${FILTER_MENU_CONFIG.Z_INDEX.DIALOG};
  `,
  DialogContent: styled(DialogContent)`
    ${({ theme }) => css`
      box-sizing: border-box;
      display: flex;
      flex-flow: column nowrap;
      position: fixed;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: ${theme.colors.background.base};
      padding: ${theme.padding.md} 32px;
      transform: translateX(${FILTER_MENU_CONFIG.ANIMATION.TRANSFORM_CLOSED});

      &.filter-dialog {
        &-enter {
          transform: translateX(${FILTER_MENU_CONFIG.ANIMATION.TRANSFORM_CLOSED});
        }

        &-enter-active {
          transform: translateX(${FILTER_MENU_CONFIG.ANIMATION.TRANSFORM_OPEN});

          @media ${media(theme).noMotionPreference} {
            transition: transform ${FILTER_MENU_CONFIG.ANIMATION.DURATION_MS}ms
              ${FILTER_MENU_CONFIG.ANIMATION.EASE_IN};
          }
        }

        &-enter-done,
        &-exit {
          transform: translateX(${FILTER_MENU_CONFIG.ANIMATION.TRANSFORM_OPEN});
        }

        &-exit-active {
          transform: translateX(${FILTER_MENU_CONFIG.ANIMATION.TRANSFORM_CLOSED});

          @media ${media(theme).noMotionPreference} {
            transition: transform ${FILTER_MENU_CONFIG.ANIMATION.DURATION_MS}ms
              ${FILTER_MENU_CONFIG.ANIMATION.EASE_OUT};
          }
        }
      }

      @media ${media(theme).greaterThan('sm')} {
        width: ${FILTER_MENU_CONFIG.DIMENSIONS.DESKTOP_DIALOG_WIDTH}px;
      }
    `}
  `,
  DialogHeader: styled.div`
    ${({ theme }) => css`
      display: flex;
      gap: ${theme.size[2]};
      justify-content: space-between;
      align-items: center;
      height: 24px;
      margin-bottom: ${theme.spacing.y.lg};
    `}
  `,
  DialogFooter: styled.div`
    ${({ theme }) => css`
      display: flex;
      gap: ${theme.size[2]};
      margin: ${theme.size[0]} -${theme.size[8]};
      border-top: ${theme.borders.width.xs} solid ${theme.colors.border.subtle};
      padding: ${theme.padding.sm} ${theme.size[8]} ${theme.padding.none} ${theme.size[8]};

      button:only-child {
        width: 100%;
      }

      button:nth-child(2) {
        width: 50%;
      }
    `}
  `,
  FiltersAndViewOptions: styled.div`
    width: 100%;
    box-sizing: border-box;
  `,
  FilterButtonWrapper: styled.div`
    ${({ theme }) => css`
      &[data-stuck='true'] {
        padding: 0;
      }

      @media ${media(theme).noMotionPreference} {
        will-change: padding;
        transition: padding 100ms ease;
      }
    `}
  `,
  FilterButton: styled.button`
    ${({ theme }) => css`
      display: flex;
      align-items: baseline;
      gap: ${theme.size['0-5']};
      font-size: ${theme.font.size[3]};
      font-weight: ${theme.variables.font.weight.medium};
      line-height: 24px;
      color: ${theme.colors.content.primary};

      svg {
        align-self: center;
        width: ${FILTER_MENU_CONFIG.DIMENSIONS.ICON_SIZE}px;
        height: ${FILTER_MENU_CONFIG.DIMENSIONS.ICON_SIZE}px;
      }
    `}
  `,
  HeaderLabel: styled.h3`
    ${({ theme }) => css`
      width: 100%;
      text-transform: uppercase;
      color: ${theme.colors.content.primary};
      font-weight: ${theme.variables.font.weight.bold};
      font-size: ${theme.font.size[6]};
    `}
  `,
  ItemWrapper: styled.div`
    height: 100%;
  `,
  InnerWrapper: styled.div`
    ${({ theme }) => css`
      background: ${theme.colors.background.base};
      flex-grow: 1;
      max-height: calc(100% - 49px);
      position: relative;
      overflow-x: hidden;
    `}
  `,
  SortByDropDown: styled.div`
    ${({ theme }) => css`
      display: flex;
      flex-direction: column;
      gap: ${theme.size[3]};
      box-sizing: border-box;
      min-width: ${FILTER_MENU_CONFIG.DIMENSIONS.MIN_DROPDOWN_WIDTH}px;
      padding: ${theme.padding.md} ${theme.padding.md} ${theme.padding.lg} ${theme.padding.md};
      position: absolute;
      right: 0;
      background: ${({ theme }) => theme.colors.background.base};
      z-index: ${FILTER_MENU_CONFIG.Z_INDEX.DROPDOWN};
      max-height: calc(100vh - 240px);
      overflow-y: auto;
      overflow-x: hidden;
    `}
  `,
  SortByItem: styled.button<{ $selected: boolean }>`
    ${({ theme }) => css`
      position: relative;
      min-height: 24px;
      padding-left: 32px;
      width: 100%;
      box-sizing: border-box;
      overflow: hidden;
      text-overflow: ellipsis;
      color: ${theme.colors.content.primary};
      white-space: nowrap;
      font-weight: ${theme.variables.font.weight.regular};
      text-align: left;

      ${roundSelectionIndicatorStyles}
    `}
  `,
  SortBySelectBox: styled.button`
    ${({ theme }) => css`
      display: flex;
      align-items: center;
      gap: ${theme.size[1]};
      height: 20px;
      font-weight: ${theme.variables.font.weight.medium};
      font-size: ${theme.font.size[3]};
      color: ${theme.colors.content.primary};

      svg {
        width: ${FILTER_MENU_CONFIG.DIMENSIONS.ICON_SIZE}px;
        height: ${FILTER_MENU_CONFIG.DIMENSIONS.ICON_SIZE}px;
      }
    `}
  `,
  SortingOptionWrapper: styled.div`
    display: none;

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
      white-space: nowrap;
    }
  `,
};
